import React, { useEffect } from 'react'
import { createBrowserRouter, RouterProvider, Outlet, redirect, useOutletContext } from 'react-router-dom'
import { debugMessage } from './common'
import { versionChecker } from './versionChecker'
import { authenticator } from './authenticator'

import AppContextProvider from './AppContextProvider'

import { Background } from './components/Background/Background'
import { InitialLoader } from './components/Home/InitialLoader'
import { hideBootSplash } from './lib/bootSplash'
import { persistIncomingAction, shouldRedirectHomeToAction } from './lib/pendingAction'
import { Login } from './Login'
import { Logout } from './Logout'
import { Action } from './Action'
import { QuickStart } from './components/QuickStart/QuickStart'
import { ErrorPage } from './components/ErrorPage/ErrorPage'

import 'semantic-ui-less/semantic.less'
import './App.css'
import './darkMode.css'

function lazyNamed(importer, exportName) {
  return () => importer().then((mod) => ({ Component: mod[exportName] }))
}

// Nested routes under /home/account must keep Home's outlet context (user, units).
// A bare <Outlet /> replaces that context with undefined, so My Profile thinks
// there is no selected unit / customer account.
function ForwardOutlet() {
  return <Outlet context={useOutletContext()} />
}

// Skip repeating the root loader on in-app /home navigations (help, preferences, …).
// Still revalidate `/` → `/home` so the redirect is followed; that second run is cheap
// because rootLoaderCompleted skips auth/version work.
function shouldRevalidateRoot({ currentUrl, nextUrl }) {
  const underHome = (url) => url.pathname === '/home' || url.pathname.startsWith('/home/')
  const underQuickStart = (url) => url.pathname === '/quick-start' || url.pathname.startsWith('/quick-start/')
  if (underHome(currentUrl) && underHome(nextUrl)) return false
  if (underQuickStart(currentUrl) && underQuickStart(nextUrl)) return false
  return true
}

// The `/` loader redirects to `/home`. React Router may run this loader again
// for that navigation; skip the expensive work the second time this tab.
let rootLoaderCompleted = false

const router = createBrowserRouter([
  {
    path: '/',
    shouldRevalidate: shouldRevalidateRoot,
    loader: async ({ request }) => {
      const path = new URL(request.url).pathname
      const isLogin = path === '/login' || path.startsWith('/login/')
      const isAction = path === '/action' || path.startsWith('/action/')
      const isQuickStart = path === '/quick-start' || path.startsWith('/quick-start/')
      const isLogout = path === '/logout' || path.startsWith('/logout/')
      const isHome = path === '/home' || path.startsWith('/home/')

      persistIncomingAction(request.url)

      const isAuthenticated = await authenticator.isAuthenticated()

      if (isLogin) return true

      if (isAction || isQuickStart) {
        const hasJwt = Boolean(authenticator.getToken()?.jwt)
        if (!isAuthenticated && !hasJwt) {
          debugMessage('Pending action requires sign in, authenticator.signIn()', 'router')
          await authenticator.signIn()
        }
        return true
      }

      if (isAuthenticated && isLogout) return true

      if (!rootLoaderCompleted) {
        const latestVersion = await versionChecker()
        if (!latestVersion) {
          debugMessage('Old version, redirecting to /login', 'router')
          return redirect('/login')
        }

        if (!isAuthenticated) {
          debugMessage('Not authenticated, authenticator.signIn()', 'router')
          await authenticator.signIn()
        }

        rootLoaderCompleted = true
      }

      if (isHome) {
        if (shouldRedirectHomeToAction(request.url)) return redirect('/action')
        // Deep-linked overlay routes should not reopen on refresh — Home loads
        // behind the modal. In-app navigations skip this loader (shouldRevalidateRoot).
        if (path === '/home/account/preferences/account-details') {
          debugMessage('Closing My Profile modal on page load', 'router')
          return redirect('/home')
        }
        return true
      }

      debugMessage('Redirecting to /home', 'router')
      return redirect('/home')
    },
    errorElement: <ErrorPage />,
    element: (
      <AppContextProvider>
        <Layout />
      </AppContextProvider>
    ),
    children: [
      {
        path: 'select-tariff',
        loader: () => {
          localStorage.setItem('openSmartSchedule', 'true')
          return redirect('/home')
        }
      },
      {
        path: 'home',
        lazy: lazyNamed(() => import('./components/Home/Home'), 'Home'),
        children: [
          {
            path: 'account',
            element: <ForwardOutlet />,
            children: [
              {
                path: 'release-notes',
                lazy: lazyNamed(() => import('./components/ReleaseNotes/ReleaseNotes'), 'ReleaseNotes')
              },
              {
                path: 'preferences',
                lazy: lazyNamed(() => import('./components/Preferences/Preferences'), 'Preferences'),
                children: [
                  {
                    path: 'account-details',
                    lazy: lazyNamed(() => import('./components/Preferences/CustomerAccountSettings'), 'CustomerAccountSettings')
                  },
                  {
                    path: 'severe-weather-auto-charge',
                    lazy: lazyNamed(() => import('./components/Preferences/GatewaySettings'), 'GatewaySettings')
                  },
                  {
                    path: 'power-cut-preferences',
                    loader: () => redirect('/home/account/preferences/severe-weather-auto-charge')
                  },
                  {
                    path: 'gateway',
                    loader: () => redirect('/home/account/preferences/severe-weather-auto-charge')
                  }
                ]
              }
            ]
          },
          {
            path: 'help',
            lazy: lazyNamed(() => import('./components/Help/Help'), 'Help')
          },
          {
            path: 'preferences',
            lazy: lazyNamed(() => import('./components/Preferences/Preferences'), 'Preferences'),
            children: [
              {
                path: 'severe-weather-auto-charge',
                lazy: lazyNamed(() => import('./components/Preferences/GatewaySettings'), 'GatewaySettings')
              },
              {
                path: 'power-cut-preferences',
                loader: () => redirect('/home/preferences/severe-weather-auto-charge')
              },
              {
                path: 'gateway',
                loader: () => redirect('/home/preferences/severe-weather-auto-charge')
              }
            ]
          }
        ]
      },
      {
        path: 'quick-start',
        element: <QuickStart />
      },
      {
        path: 'action',
        element: <Action />
      },
      {
        path: 'login',
        element: <Login />
      },
      {
        path: 'logout',
        element: <Logout />
      }
    ]
  }
])

export default function App() {
  useEffect(() => {
    debugMessage('Mounted', 'App')
    document.body.classList.add('initial-load')
    // Home's InitialLoader used to be the only thing that removed #boot-splash.
    // Quick Start never mounts that loader, so the HTML overlay sat on top of
    // the wizard after POST /users/auth with no further network and no errors.
    hideBootSplash()
  }, [])

  return <RouterProvider router={router} fallbackElement={<InitialLoader loading />} />
}

function Layout() {
  return (
    <div className="App">
      <Background />
      <Outlet />
    </div>
  )
}
